import { useEffect } from 'react';
import { GamepadBridge, readPads } from './gamepad';

/**
 * Pętla odpytująca pady i wysyłająca syntetyczne zdarzenia klawiatury.
 *
 * Odpalamy ją **dopiero po podłączeniu pada**, a nie od startu aplikacji: pętla
 * `requestAnimationFrame` bez podłączonego kontrolera to sześćdziesiąt przebiegów
 * na sekundę po nic, a w przeglądarce na telefonie także zużycie baterii.
 * Przeglądarka i tak nie pokaże padów, dopóki ktoś nie naciśnie na nich przycisku —
 * to wymóg bezpieczeństwa, nie nasz wybór.
 *
 * Zdarzenia lecą na `window` z `bubbles: true`, więc trafiają dokładnie tam, gdzie
 * klawiatura: do nawigacji strzałkami, do skrótów odtwarzacza i — przy `Enter` —
 * do elementu z fokusem, bo to jego domyślne zachowanie.
 */
export function useGamepad(): void {
  useEffect(() => {
    if (typeof navigator.getGamepads !== 'function') return;

    const bridge = new GamepadBridge();
    let frame: number | null = null;

    function tick(): void {
      const keys = bridge.step(readPads(navigator.getGamepads()), performance.now());

      for (const key of keys) {
        /**
         * `Enter` na elemencie z fokusem oddajemy przeglądarce.
         *
         * Syntetyczne `keydown` **nie klika** przycisku — zaufane zdarzenia robi
         * tylko użytkownik. Dlatego dla zatwierdzenia wołamy `click()` wprost, a dla
         * reszty wystarcza zdarzenie, bo nasze hooki słuchają `keydown`.
         */
        if (key === 'Enter' && document.activeElement instanceof HTMLElement) {
          document.activeElement.click();
          continue;
        }
        window.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
      }

      frame = requestAnimationFrame(tick);
    }

    function start(): void {
      if (frame === null) frame = requestAnimationFrame(tick);
    }

    function stop(): void {
      // Zatrzymujemy dopiero, gdy odłączono ostatni pad: przy dwóch kontrolerach
      // odłączenie jednego nie może zabrać sterowania drugiemu.
      const anyConnected = [...navigator.getGamepads()].some((pad) => pad?.connected === true);
      if (anyConnected || frame === null) return;
      cancelAnimationFrame(frame);
      frame = null;
    }

    // Pad podłączony przed wejściem na stronę też się liczy — zdarzenie
    // `gamepadconnected` przyszłoby dopiero po naciśnięciu przycisku.
    if ([...navigator.getGamepads()].some((pad) => pad?.connected === true)) start();

    window.addEventListener('gamepadconnected', start);
    window.addEventListener('gamepaddisconnected', stop);
    return () => {
      window.removeEventListener('gamepadconnected', start);
      window.removeEventListener('gamepaddisconnected', stop);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);
}
